/**
 * Network Page
 * Dedicated page for network visualization
 */

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Home, Calendar, Database } from "lucide-react";
import { parseYears, buildPersonToItems } from "../utils/yearParsing";
import { useTeaCeremonyData } from "../hooks/useTeaCeremonyData";
import NetworkVisualization from "../components/NetworkVisualization";
import { transformPersonData } from "../utils/personDataProcessing";
import { transformItemData } from "../utils/itemDataProcessing";

const NetworkPage = () => {
  const { people, items, itemSlugs, personSlugs, isLoading, error } = useTeaCeremonyData();
  const [query, setQuery] = useState("");
  const [range, setRange] = useState({ min: null, max: null });
  const navigate = useNavigate();

  // Build person-to-items mapping from loaded data
  const personToItems = useMemo(() => buildPersonToItems(items), [items]);

  // Enrich people data with parsed years and items
  const enriched = useMemo(() => {
    return (people || []).map((p, idx) => {
      const yrs = parseYears(p.years);
      const linkedItems =
        personToItems[p.name_jp] || personToItems[p.name_romaji] || [];
      return { ...p, _idx: idx, yearsParsed: yrs, items: linkedItems };
    });
  }, [people, personToItems]);

  // Calculate timeline bounds
  const bounds = useMemo(() => {
    let min = Infinity, max = -Infinity;

    enriched.forEach((p) => {
      const { start, end } = p.yearsParsed || {};
      const a = start ?? end;
      const b = end ?? start;
      if (a != null) min = Math.min(min, a);
      if (b != null) max = Math.max(max, b);
    });

    if (!isFinite(min) || !isFinite(max)) {
      min = 1400;
      max = new Date().getFullYear();
    }

    return { min, max };
  }, [enriched]);

  // Filter people
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    const min = range.min ?? bounds.min;
    const max = range.max ?? bounds.max;

    return enriched
      .filter((p) => {
        const { start, end } = p.yearsParsed || {};
        const lo = start ?? end ?? -Infinity;
        const hi = end ?? start ?? Infinity;

        const within = (() => {
          if (lo === -Infinity && hi === Infinity) return false;
          return hi >= min && lo <= max;
        })();

        if (!within) return false;
        if (!q) return true;

        const hay = [
          p.name_jp,
          p.name_romaji,
          p.region,
          p.description,
          (p.items || []).join(" "),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ay = a.yearsParsed.start ?? a.yearsParsed.end ?? 9999;
        const by = b.yearsParsed.start ?? b.yearsParsed.end ?? 9999;
        return ay - by;
      });
  }, [enriched, query, range, bounds]);

  // Prepare network data using filtered results
  const networkData = useMemo(() => {
    if (!filtered.length || !items) {
      return null;
    }

    try {
      // Transform filtered data to network format
      const networkPeople = filtered.map(person => ({
        id: person.name_romaji || person.name_jp,
        name: person.name_romaji || person.name_jp,
        nameJp: person.name_jp,
        years: person.years,
        region: person.region,
        description: person.description,
        type: 'person'
      }));

      const itemNames = Object.keys(items).slice(0, 30);
      const networkItems = itemNames.map(itemName => ({
        id: itemName,
        name: itemName,
        type: 'item'
      }));

      // Create relationships
      const relationships = [];

      // Add person-item relationships from items.json (comprehensive)
      Object.entries(items).forEach(([itemName, connections]) => {
        if (itemNames.includes(itemName)) {
          connections.forEach(conn => {
            const personName = conn.person;
            const matchedPerson = filtered.find(p => {
              return p.name_jp === personName ||
                     p.name_romaji === personName ||
                     personName.includes(p.name_jp) ||
                     personName.includes(p.name_romaji) ||
                     (personName.includes('(') && personName.split('(')[0].trim() === p.name_jp) ||
                     (personName.includes('(') && personName.split('(')[1]?.replace(')', '').trim() === p.name_romaji);
            });

            if (matchedPerson) {
              relationships.push({
                source: matchedPerson.name_romaji || matchedPerson.name_jp,
                target: itemName,
                type: 'ownership',
                label: conn.connection,
                weight: 0.8
              });
            }
          });
        }
      });

      // Add person-person relationships
      filtered.forEach(person => {
        (person.connections || []).forEach(connectionName => {
          const connectedPerson = filtered.find(p =>
            p.name_jp === connectionName || p.name_romaji === connectionName
          );
          if (connectedPerson) {
            relationships.push({
              source: person.name_romaji || person.name_jp,
              target: connectedPerson.name_romaji || connectedPerson.name_jp,
              type: 'connection',
              weight: 1.0
            });
          }
        });
      });

      return {
        people: networkPeople,
        items: networkItems,
        relationships: relationships
      };
    } catch (error) {
      console.error('Network data preparation error:', error);
      return null;
    }
  }, [filtered, items]);

  // Handle context menu actions
  const handleContextMenuAction = useCallback((action, node) => {
    if (action === 'show-details') {
      // Navigate directly to detail page
      if (node.type === 'person') {
        const originalPerson = people.find(p =>
          p.name_jp === node.nameJp ||
          p.name_romaji === node.name ||
          p.name_jp === node.name
        );

        if (originalPerson) {
          const personData = transformPersonData(originalPerson, items, itemSlugs);
          navigate(`/person/${personData.id}`);
        }
      } else if (node.type === 'item') {
        const itemConnections = items[node.name] || [];
        const itemData = transformItemData(node.name, itemConnections, people, personSlugs);
        const canonicalId = (itemSlugs && itemSlugs[node.name]) || itemData.id;
        navigate(`/item/${canonicalId}`);
      }
    }
    // Other actions can be handled here
  }, [people, items, itemSlugs, personSlugs, navigate]);

  // Handle node clicks for navigation to detail pages
  const handleNodeClick = useCallback((node) => {
    if (node.type === 'person') {
      // Find the original person data to get the correct ID
      const originalPerson = people.find(p =>
        p.name_jp === node.nameJp ||
        p.name_romaji === node.name ||
        p.name_jp === node.name
      );

      if (originalPerson) {
        const personData = transformPersonData(originalPerson, items, itemSlugs);
        navigate(`/person/${personData.id}`);
      }
    } else if (node.type === 'item') {
      // Transform item name to ID format and prefer stored slugs
      const itemConnections = items[node.name] || [];
      const itemData = transformItemData(node.name, itemConnections, people, personSlugs);
      const canonicalId = (itemSlugs && itemSlugs[node.name]) || itemData.id;
      navigate(`/item/${canonicalId}`);
    }
  }, [people, items, itemSlugs, personSlugs, navigate]);

  // Auto-set initial range to calculated bounds
  useEffect(() => {
    if (range.min == null) setRange(bounds);
  }, [bounds.min, bounds.max]);

  // Handle loading state
  if (isLoading) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="text-center py-12">
          <div className="text-lg font-semibold">Loading network data...</div>
          <div className="text-sm text-gray-600 mt-2">
            Please wait while we load the tea ceremony data.
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="text-center py-12">
          <div className="text-lg font-semibold text-red-600">
            Error loading data
          </div>
          <div className="text-sm text-gray-600 mt-2">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-md hover:bg-gray-100"
          >
            <Home size={16} />
            Home
          </Link>
          <span className="text-gray-300">•</span>
          <h1 className="text-2xl font-semibold text-gray-800">Network View</h1>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/timeline"
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
          >
            <Calendar size={16} />
            Timeline View
          </Link>
          <Link
            to="/editor"
            className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200"
          >
            <Database size={16} />
            Edit Data
          </Link>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Explore the interconnected web of relationships between people and objects in tea ceremony history.
        Drag nodes to reposition, zoom with mouse wheel, and right-click for actions.
      </p>

      <div className="mb-6 grid md:grid-cols-3 gap-3 items-end">
        <div className="md:col-span-2">
          <label htmlFor="year-range" className="text-xs font-medium">
            Year Range
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              className="w-32 border rounded-lg p-2"
              placeholder="Min year"
              value={range.min ?? ""}
              onChange={(e) =>
                setRange((r) => ({
                  ...r,
                  min: parseInt(e.target.value || "", 10),
                }))
              }
            />
            <span className="text-gray-500">to</span>
            <input
              type="number"
              className="w-32 border rounded-lg p-2"
              placeholder="Max year"
              value={range.max ?? ""}
              onChange={(e) =>
                setRange((r) => ({
                  ...r,
                  max: parseInt(e.target.value || "", 10),
                }))
              }
            />
            <button
              className="ml-2 px-3 py-2 rounded-lg border text-xs hover:bg-gray-50"
              onClick={() => setRange(bounds)}
            >
              Reset
            </button>
          </div>
        </div>
        <div>
          <label htmlFor="search" className="text-xs font-medium">
            Search
          </label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-500" />
            <input
              id="search"
              className="w-full border rounded-lg pl-8 p-2"
              placeholder="name, region, item, school…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Network Visualization */}
      <div className="h-[700px] border border-gray-200 rounded-lg overflow-hidden bg-white">
        {networkData ? (
          <div className="h-full w-full">
            <div className="p-4 border-b bg-gray-50">
              <div className="text-sm font-medium text-gray-700">Network Visualization</div>
              <div className="text-xs text-gray-500">
                {networkData.people.length} people • {networkData.items.length} items • {networkData.relationships.length} relationships
              </div>
            </div>
            <div className="h-[calc(100%-60px)]">
              <NetworkVisualization
                data={networkData}
                width={1360}
                height={640}
                showLabels={true}
                showTooltips={true}
                enableZoom={true}
                enableSelection={true}
                enableContextMenu={true}
                showControls={true}
                autoCenter={true}
                onNodeClick={handleNodeClick}
                onContextMenuAction={handleContextMenuAction}
                nodeColors={{
                  person: '#8b4513',
                  item: '#daa520'
                }}
                edgeColors={{
                  connection: '#a0522d',
                  ownership: '#cd853f'
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-lg font-medium text-gray-700 mb-2">
                Network View Not Available
              </div>
              <div className="text-sm text-gray-500">
                No data available for network visualization.
                {!filtered.length ? ' Apply fewer filters to see network connections.' : ' Please check the data source.'}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
        <span>
          Network: {networkData?.people?.length || 0} people, {networkData?.items?.length || 0} items, {networkData?.relationships?.length || 0} connections
        </span>
        <span className="text-blue-600">
          Tip: Right-click nodes for actions • Use mouse wheel to zoom • Drag to pan
        </span>
      </div>
    </div>
  );
};

export default NetworkPage;